/**
 * Global search behind the entry-page field (Beta-1 plan P6-03 / P6-04 / P5-11; C0826 "Texas City
 * explosion → takes me right to that case study"). Pure module — the hook in `useGlobalSearch.js`
 * feeds it data and navigates.
 *
 * Entries: { kind, id, title, subtitle, text, path, group, keywords[] }
 *   kind ∈ page | help | caseStudy | survey | condition | member | intent
 * Scoring (per entry): exact title match 100 · title starts with query 80 · every term in title 60 ·
 * every term in keywords 50 · every term in subtitle/text 30 · full-document hit 20 (case studies,
 * via P5-08 text). A "strong" single match (score ≥ 60 and ≥ 20 above the runner-up) navigates
 * directly on Enter.
 */
import { parseQuery, matchesAll, snippetsFor } from '../../assistants/drew/utils/textSearch.js';

export const KIND_LABELS = Object.freeze({
  intent: 'Actions',
  page: 'Pages',
  caseStudy: 'Case studies',
  condition: 'OE Conditions',
  survey: 'Surveys',
  help: 'Help & Docs',
  member: 'Members',
});
export const KIND_ORDER = Object.freeze(['intent', 'page', 'caseStudy', 'condition', 'survey', 'help', 'member']);

const DA = '/drew';

/** Actions a person types rather than a page name ("log an OE condition", "take a survey"). */
export const INTENTS = Object.freeze([
  { id: 'log-condition', title: 'Log an OE Condition', subtitle: 'OE Condition Entry Portal', path: `${DA}/rollup/trl-entry`, keywords: ['log', 'oe condition', 'condition', 'drift', 'accumulation', 'trap', 'roadblock', 'landmine', 'entry', 'report a condition', 'submit'] },
  { id: 'api-docs', title: 'API documentation', subtitle: 'Connect your systems to REPSit.ai', path: '/api-docs', keywords: ['api', 'integration', 'webhook', 'developer', 'key', 'openapi'] },
  { id: 'take-survey', title: 'Take a survey', subtitle: 'D&A Perspectives Surveys', path: `${DA}/surveys`, keywords: ['survey', 'take survey', 'perspectives', 'respond'] },
  { id: 'upload-document', title: 'Upload a document', subtitle: 'Document Upload Portal', path: `${DA}/documents`, keywords: ['upload', 'document', 'pdf', 'report', 'incident report', 'add documents'] },
  { id: 'customize-dashboard', title: 'Customize my dashboard', subtitle: 'REPSit Dashboard → Customize', path: '/dashboard', keywords: ['customize', 'widgets', 'dashboard', 'layout', 'cards'] },
  { id: 'import-org', title: 'Import my organization', subtitle: 'Settings → Organization → Import', path: '/settings/organization', keywords: ['import', 'org chart', 'roster', 'organization', 'structure', 'members'] },
  { id: 'thresholds', title: 'Set warning thresholds', subtitle: 'Settings → DREW → Warning Thresholds', path: '/settings/drew', keywords: ['threshold', 'warning', 'thresholds', 'alert level'] },
  { id: 'feedback', title: 'Tell the Build Team', subtitle: 'Send feedback, a bug or an idea', path: null, action: 'feedback', keywords: ['feedback', 'bug', 'idea', 'help me', 'stuck', 'build team', 'contact'] },
]);

/** Build page entries from the two sidebars' sections ([{ key, label, items: [{ text, path, secondary }] }]). */
export function pageEntries(sections = [], { group = 'platform' } = {}) {
  const out = [];
  for (const sec of sections || []) {
    for (const item of sec.items || []) {
      if (!item.path || item.disabled) continue;
      out.push({
        kind: 'page', id: `page:${item.id || item.path}`, title: item.text, subtitle: sec.label ? `${group === 'drew' ? 'DREW · ' : ''}${sec.label}` : group === 'drew' ? 'DREW' : 'REPSit.ai',
        text: item.secondary || '', path: item.path.split('#')[0], group, keywords: [item.secondary || ''],
      });
    }
  }
  return out;
}

export function helpEntries(entries = [], { helpSlug } = {}) {
  return (entries || []).map((e) => ({
    kind: 'help', id: `help:${e.q}`, title: e.q, subtitle: e.kind === 'faq' ? 'FAQ' : e.kind === 'howto' ? 'How do I…' : 'Core concept',
    text: Array.isArray(e.a) ? e.a.join(' ') : String(e.a || ''), path: `/help?open=${helpSlug ? helpSlug(e.q) : ''}`, group: e.group || 'drew', keywords: [],
  }));
}

export function caseStudyEntries(caseStudies = [], fullTextById = {}) {
  return (caseStudies || []).map((cs) => ({
    kind: 'caseStudy', id: `cs:${cs.id}`, title: cs.title || 'Untitled case study',
    subtitle: [cs.company, cs.location, cs.industry].filter(Boolean).join(' · '),
    text: [cs.summary, ...(Array.isArray(cs.keyFindings) ? cs.keyFindings.map((f) => (typeof f === 'string' ? f : f.finding || f.text || '')) : []), ...(Array.isArray(cs.tags) ? cs.tags : [])].filter(Boolean).join(' '),
    fullText: fullTextById[cs.id] || '',
    path: `${DA}/case-studies/${cs.id}`, group: 'platform', keywords: [],
  }));
}

export function surveyEntries(surveys = []) {
  return (surveys || []).map((s) => ({
    kind: 'survey', id: `survey:${s.id}`, title: s.title || 'Untitled survey', subtitle: s.status ? `Survey · ${s.status}` : 'Survey',
    text: s.description || '', path: `${DA}/surveys/${s.id}`, group: 'drew', keywords: [],
  }));
}

export function conditionEntries(conditions = []) {
  return (conditions || []).map((c) => ({
    kind: 'condition', id: `trl:${c.id}`, title: c.conditionId ? `${c.conditionId} — ${c.title || ''}` : c.title || 'OE Condition',
    subtitle: [c.type, c.status].filter(Boolean).join(' · '), text: c.description || '',
    path: `${DA}/rollup/trl-entry?condition=${c.id}`, group: 'drew', keywords: [c.conditionId || ''],
  }));
}

export function memberEntries(members = []) {
  return (members || []).map((m) => ({
    kind: 'member', id: `member:${m.uid || m.id || m.email}`, title: m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email || 'Member',
    subtitle: [m.title, m.roleLabel || m.role, m.scopeName].filter(Boolean).join(' · '), text: m.email || '',
    path: `/settings/organization?member=${encodeURIComponent(m.uid || m.id || '')}`, group: 'platform', keywords: [m.email || ''],
  }));
}

export function intentEntries() {
  return INTENTS.map((i) => ({ kind: 'intent', id: `intent:${i.id}`, title: i.title, subtitle: i.subtitle, text: '', path: i.path, action: i.action || null, group: 'platform', keywords: i.keywords }));
}

function scoreEntry(entry, terms, q) {
  const title = (entry.title || '').toLowerCase();
  const kw = (entry.keywords || []).join(' ').toLowerCase();
  const sub = (entry.subtitle || '').toLowerCase();
  const text = (entry.text || '').toLowerCase();
  let score = 0;
  if (title === q) score = 100;
  else if (title.startsWith(q)) score = 80;
  else if (matchesAll(title, terms)) score = 60;
  else if (title.includes(q)) score = 55;
  else if (matchesAll(kw, terms) || kw.includes(q)) score = 50;
  else if (matchesAll(`${sub} ${text}`, terms)) score = 30;
  else if (matchesAll(`${title} ${kw} ${sub} ${text}`, terms)) score = 25;
  else if (entry.fullText && matchesAll(entry.fullText, terms)) score = 20;
  if (entry.kind === 'intent' && score >= 50) score += 5; // an action wins ties with a page of the same name
  return score;
}

/**
 * @returns {{ results: Array, groups: Array<{kind,label,items}>, best: Object|null, terms: string[] }}
 */
export function searchIndex(entries, query, { limitPerKind = 5 } = {}) {
  const terms = parseQuery(query);
  const q = String(query || '').trim().toLowerCase();
  if (!terms.length) return { results: [], groups: [], best: null, terms };
  const scored = [];
  const seenIds = new Set();
  for (const e of entries || []) {
    if (seenIds.has(e.id)) continue; // the same page can sit in both sidebars (ACCESS LEVELS)
    seenIds.add(e.id);
    const score = scoreEntry(e, terms, q);
    if (score > 0) {
      const snippet = score <= 30 ? snippetsFor(score === 20 ? e.fullText : e.text, terms, { max: 1, radius: 60 })[0] || null : null;
      scored.push({ ...e, score, snippet });
    }
  }
  scored.sort((a, b) => b.score - a.score || KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.title.localeCompare(b.title));
  const groups = KIND_ORDER.map((kind) => ({ kind, label: KIND_LABELS[kind], items: scored.filter((r) => r.kind === kind).slice(0, limitPerKind) })).filter((g) => g.items.length);
  const best = scored.length && scored[0].score >= 60 && (scored.length === 1 || scored[0].score - scored[1].score >= 20) ? scored[0] : null;
  return { results: scored, groups, best, terms };
}
